package com.exura.components.buttons

import com.exura.primary
import com.exura.ui.Dimens
import com.exura.ui.Durations
import com.exura.warning
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.disabled
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.overlay
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.div
import org.jetbrains.compose.web.css.plus
import org.jetbrains.compose.web.css.times

sealed interface ButtonKind : ComponentKind

val AppButtonStyle = CssStyle<ButtonKind> {
    val palette = colorMode.toPalette()
    val buttonMinWidth = 2 * Dimens.buttonBorderWidth +
            2 * Dimens.buttonIndent +
            Dimens.iconRelSizeDefault +
            Dimens.iconTextRelGapSizeDefault +
            Dimens.buttonMinTextSize
    base {
        Modifier
            .color(palette.color)
            .height(Dimens.buttonHeight)
            .minWidth(buttonMinWidth)
            .padding(leftRight = Dimens.buttonIndent)
            .fontSize(Dimens.buttonFontSize)
            .fontWeight(FontWeight.Medium)
            .justifyContent(JustifyContent.Center)
            .cursor(Cursor.Pointer)
            .transition(Transition.all(Durations.short))
            .border(width = Dimens.buttonBorderWidth, style = LineStyle.Solid, color = Colors.Transparent)
            .borderRadius(Dimens.buttonBorderRadius)
    }

    active {
        Modifier.opacity(0.8)
    }

    disabled {
        Modifier
            .backgroundColor(palette.overlay)
            .opacity(0.5)
            .cursor(Cursor.NotAllowed)
    }
}

val ButtonRoundVariant = AppButtonStyle.addVariant {
    base {
        Modifier
            .height(Dimens.buttonHeight)
            .minWidth(Dimens.buttonHeight)
            .width(Dimens.buttonHeight)
            .padding(Dimens.spacingNone)
            .borderRadius(Dimens.buttonHeight / 2)
    }
}

val ButtonPrimaryVariant = AppButtonStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .backgroundColor(palette.primary)
            .color(palette.background)
    }
    hover {
        Modifier.backgroundColor(palette.primary.toRgb().copyf(alpha = 0.8f))
    }
}

val ButtonOutlineVariant = AppButtonStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .border(width = Dimens.buttonBorderWidth, style = LineStyle.Solid, color = palette.primary)
            .color(palette.primary)
    }
    hover {
        Modifier
            .backgroundColor(palette.primary.toRgb().copyf(alpha = 0.1f))
    }
}

val ButtonGhostVariant = AppButtonStyle.addVariant {
    val palette = colorMode.toPalette()
    hover {
        Modifier
            .backgroundColor(palette.color.toRgb().copyf(alpha = 0.05f))
    }
}

val ButtonDangerVariant = AppButtonStyle.addVariant {
    val palette = colorMode.toPalette()
    val highlightedBgColor = palette.warning.toRgb().copyf(alpha = 0.8f)

    hover {
        Modifier.backgroundColor(highlightedBgColor)
    }
}

val ButtonDisabledVariant = AppButtonStyle.addVariant {
    val palette = colorMode.toPalette()
    val disabledModifier = Modifier
        .backgroundColor(palette.overlay.toRgb().copyf(alpha = 0.3f))
        .opacity(0.9)
        .cursor(Cursor.NotAllowed)

    base {
        disabledModifier
    }

    hover {
        disabledModifier
    }
}
